import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { openMessage } from '../../store/slices/showMessageSlice';
import { useDispatch } from 'react-redux';

// Helper function to escape special characters for CSS selectors
const escapeSelector = (str) => {
    if (typeof str !== 'string') return '';
    return str.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
};

// Helper function to safely query an element
const safeQuerySelector = (selector) => {
    try {
        return document.querySelector(selector);
    } catch (error) {
        console.warn('FocusError: Invalid selector', selector, error);
        return null;
    }
};

const FocusError = () => {
    const dispatch = useDispatch();
    const { errors, isSubmitting, isValidating } = useFormikContext();
    
    useEffect(() => {
        try {
            if (isSubmitting && !isValidating && errors) {
                const keys = Object.keys(errors);
                if (keys.length > 0) {
                    const firstErrorKey = keys[0];
                    const firstErrorValue = errors[firstErrorKey];
                    
                    let selector = '';
                    let hasNestedError = false;
                    
                    // Check if the error value is an object (nested errors) or a string (direct error)
                    if (firstErrorValue && typeof firstErrorValue === 'object' && !Array.isArray(firstErrorValue)) {
                        const childKeys = Object.keys(firstErrorValue);
                        if (childKeys.length > 0) {
                            const escapedParent = escapeSelector(firstErrorKey);
                            const escapedChild = escapeSelector(childKeys[0]);
                            selector = `[name="${escapedParent}.${escapedChild}"]`;
                            hasNestedError = true;
                        }
                    }
                    
                    // Fallback to parent key if no nested error or nested selector fails
                    if (!selector) {
                        selector = `[name="${escapeSelector(firstErrorKey)}"]`;
                    }
                    
                    if (hasNestedError) {
                        dispatch(
                            openMessage({
                                message: 'Please complete all required fields.',
                                messageSeverity: 'warning',
                            })
                        );
                    }
                    
                    const errorElement = safeQuerySelector(selector);
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        errorElement.focus({ preventScroll: true });
                    }
                }
            }
        } catch (error) {
            console.error('FocusError: An error occurred while focusing on error field', error);
        }
    }, [errors, isSubmitting, isValidating, dispatch]);

    return null;
};

export default FocusError;